import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { type SurveyModel, type CompleteEvent } from "survey-core";
import { apiRequest } from "shared/api/apiRequest";
import { postSocietyProfileWorkflowInstanceContract } from "shared/api/contracts/society/societyId/profiles/profileId/workflow/workflowId/instance";
import { postSocietyProfileWorkflowInstanceIdContract } from "shared/api/contracts/society/societyId/profiles/profileId/workflow/workflowId/instance/workflowInstanceId";
import { postSocietyWorkflowInstanceContract } from "shared/api/contracts/society/societyId/workflow/workflowId/instance";
import { type SocietyWorkflowInstanceInvokeParamsInput } from "shared/api/types/society/[societyId]/workflow/[workflowId]/instance";
import {
  type SurveyJSFormTemplateData,
  type SurveyJSFormValuesOnCompleteCallback,
  type SurveyJSFormValuesUseQueryData,
} from "shared/components/survey-js-form/forms/useSurveyJSFormValues";
import {
  surveyJSupdateOnCompleteHtml,
  surveyJSAddSpinnerToCompletedHtml,
  surveyJSRemoveSpinnerFromCompletedHtml,
} from "shared/components/survey-js-form/SurveyJSFormComponent";
import { renderTemplateUtil } from "shared/components/template/renderTemplateUtil";
import { type FormView } from "shared/mappers/database/form/form";
import { type ProfileView } from "shared/mappers/database/profile/profile";
import { type SocietyView } from "shared/mappers/database/society/society";
import { type WorkflowView } from "shared/mappers/database/workflow/workflow";
import { type WorkflowInstanceView } from "shared/mappers/database/workflow/workflow-instance";
import { type WorkflowInstanceStopped } from "shared/workflow/workflow-instance";
import { type WorkflowInstancePayloadValueBase } from "shared/workflow/workflow-instance-payload";

export const useSurveyJSOnCompleteFunction = ({
  data,
  profile,
  setState,
  overrideCompletePageHTML,
  templateData,
  society,
  onCompleteRefreshCallback,
  onCompleteCallback,
  consumeAdminOptions,
  userhubRedirect,
}: {
  data?: SurveyJSFormValuesUseQueryData;
  profile?: ProfileView;
  setState?: string;
  overrideCompletePageHTML?: string;
  templateData?: SurveyJSFormTemplateData;
  society: SocietyView;
  onCompleteRefreshCallback?: () => Promise<void>;
  onCompleteCallback?: SurveyJSFormValuesOnCompleteCallback;
  consumeAdminOptions?: () => SocietyWorkflowInstanceInvokeParamsInput | null;
  /** Overrides the workflow wait state's userhubRedirect for this submit */
  userhubRedirect?: string;
}) => {
  const queryClient = useQueryClient();
  return useCallback(
    async (sender: SurveyModel, options: CompleteEvent): Promise<void> => {
      const adminInvokeParams = consumeAdminOptions?.() ?? undefined;
      const invokeParams =
        userhubRedirect || adminInvokeParams
          ? {
              ...(userhubRedirect ? { userhubRedirect } : {}),
              ...adminInvokeParams,
            }
          : undefined;
      const { clearSaveMessages, showSaveError } = options;
      clearSaveMessages();
      surveyJSupdateOnCompleteHtml(
        sender,
        renderTemplateUtil({
          template:
            overrideCompletePageHTML ??
            data?.internalForm?.formDesign?.completedHtml ??
            "Please wait processing... ",
          templateData,
        }),
      );
      surveyJSAddSpinnerToCompletedHtml(sender);

      if (profile?.profileId && !sender.data.profileId) {
        //TODO: Should be uneccessary, as this is in the postSocietyProfileWorkflowInstanceIdContract already
        sender.setValue("profileId", profile?.profileId);
      }
      if (data?.internalForm?.formId) {
        //TODO: Should be uneccessary, as this is in the postSocietyProfileWorkflowInstanceIdContract already
        sender.setValue("formId", data?.internalForm?.formId);
      }
      try {
        const workflowInstanceStopped: WorkflowInstanceStopped =
          await postFormDataToApi({
            internalForm: data?.internalForm,
            internalWorkflow: data?.internalWorkflow,
            internalWorkflowInstance: data?.internalWorkflowInstance,
            society,
            profile,
            setState,
            payloadValue: sender.data,
            invokeParams,
          });

        //Invalidate the form-values cache to ensure fresh data on next mount instead of pre-submit values
        await queryClient.invalidateQueries({ queryKey: ["form-values"] });

        await onCompleteRefreshCallback?.();
        //Remove the spinner from the completedHtml since we are done processing that
        surveyJSRemoveSpinnerFromCompletedHtml(sender);
        if (workflowInstanceStopped.completePageTemplate) {
          surveyJSupdateOnCompleteHtml(
            sender,
            renderTemplateUtil({
              template: workflowInstanceStopped.completePageTemplate,
              templateData: {
                ...templateData,
                workflowInstanceStopped,
              },
            }),
          );
        }
        if (onCompleteCallback) {
          surveyJSAddSpinnerToCompletedHtml(sender);
          await onCompleteCallback({
            survey: sender,
            options,
            workflowInstanceStopped,
          });
        }
      } catch (error) {
        console.error("onComplete error", error);
        if (error instanceof Error) {
          showSaveError(error.message);
        }
      }
    },
    [
      queryClient,
      data,
      profile,
      setState,
      overrideCompletePageHTML,
      templateData,
      society,
      onCompleteRefreshCallback,
      onCompleteCallback,
      consumeAdminOptions,
      userhubRedirect,
    ],
  );
};

const postFormDataToApi = async ({
  internalForm,
  internalWorkflow,
  internalWorkflowInstance,
  society,
  profile,
  setState,
  payloadValue,
  invokeParams,
}: {
  internalForm?: FormView | null;
  internalWorkflow?: WorkflowView | null;
  internalWorkflowInstance?: WorkflowInstanceView | null;
  society: SocietyView;
  profile?: ProfileView;
  setState?: string;
  payloadValue: WorkflowInstancePayloadValueBase;
  invokeParams?: SocietyWorkflowInstanceInvokeParamsInput;
}) => {
  if (internalForm?.formId === undefined) {
    throw new Error("Form is required to post form data");
  }
  if (internalWorkflow?.workflowId === undefined) {
    throw new Error("Workflow is required to post form data");
  }
  if (
    internalWorkflowInstance?.workflowInstanceId !== undefined &&
    profile?.profileId !== undefined
  ) {
    return await apiRequest({
      contract: postSocietyProfileWorkflowInstanceIdContract,
      inputs: {
        params: {
          societyId: society.societyId,
          workflowId: internalWorkflow.workflowId,
          workflowInstanceId: internalWorkflowInstance.workflowInstanceId,
          profileId: profile.profileId,
        },
        body: {
          eventName: "Submit",
          formId: internalForm.formId,
          payloadValue: payloadValue,
          workflowInstancePayloadId:
            internalWorkflowInstance.workflowInstancePayload
              ?.workflowInstancePayloadId,
          setState,
          ...(invokeParams ? { invokeParams } : {}),
        },
      },
    });
  }
  if (profile?.profileId !== undefined) {
    return await apiRequest({
      contract: postSocietyProfileWorkflowInstanceContract,
      inputs: {
        params: {
          profileId: profile?.profileId,
          societyId: society!.societyId,
          workflowId: internalWorkflow!.workflowId!,
        },
        body: {
          eventName: "Submit",
          formId: internalForm!.formId!,
          payloadValue,
          setState,
          ...(invokeParams ? { invokeParams } : {}),
        },
      },
    });
  }
  return await apiRequest({
    contract: postSocietyWorkflowInstanceContract,
    inputs: {
      params: {
        societyId: society.societyId,
        workflowId: internalWorkflow.workflowId,
      },
      body: {
        eventName: "Submit",
        formId: internalForm.formId,
        payloadValue,
        ...(invokeParams ? { invokeParams } : {}),
      },
    },
  });
};
